import { Navigate, Outlet, useLocation } from "react-router";
import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";
import { Footer } from "./Footer";
import { useAuth } from "@/hooks/useAuth";

/**
 * App chrome — left sidebar + main content + footer.
 *
 * The previous build had a 3px terracotta accent strip across the top of the
 * main content area; the user (correctly) hated it. The studio/admin accent
 * is now carried by the active sidebar item's tinted background + 2px left
 * edge, which is far more elegant. Main content sits flush against the
 * sidebar's right border with no top strip.
 *
 * The logged-in user lives in a Google/Azure-style account menu pinned to
 * the top-right via a slim, transparent <TopBar/> (no chrome, just reserves
 * a row so the avatar never collides with a page header).
 */
export function SidebarLayout() {
  const { pathname } = useLocation();
  const { user, devUser, ready } = useAuth();
  const isAdmin = pathname.startsWith("/admin");

  // Hold render briefly while Firebase resolves the persisted session.
  // Without this, the very first paint can render the layout with no auth,
  // fire queries, and 401.
  if (!ready) {
    return (
      <div className="grid min-h-screen place-items-center bg-bg-1 text-fg-3">
        Loading…
      </div>
    );
  }

  // Hard guard: every sidebar route requires *some* auth (real Firebase
  // session, or a dev persona chosen via /auth/signin's fallback). Anyone
  // hitting /catalogue, /admin/*, etc. without it bounces to the landing.
  if (!user && !devUser) {
    return <Navigate to="/" replace state={{ from: pathname }} />;
  }

  return (
    <div className="flex min-h-screen flex-col bg-bg-1">
      <div className="flex flex-1">
        <Sidebar isAdmin={isAdmin} />
        <div className="flex min-w-0 flex-1 flex-col">
          <TopBar />
          <main className="flex-1 min-w-0">
            <Outlet />
          </main>
        </div>
      </div>
      <Footer />
    </div>
  );
}
